import { ref, computed, readonly } from 'vue'
import { useRoute } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { SearchKeywordImpressionLabelsAPIClient } from 'src/biz_spa/apiClients'
import { useFlash } from 'src/biz_spa/composables'
import type { SearchKeywordImpressionLabelId, SearchKeywordImpressionLabelKeyword, SearchKeywordImpressionLabel } from 'src/biz_spa/types'

export const useSearchKeywordImpressionLabels = () => {
  const apiClient = new SearchKeywordImpressionLabelsAPIClient()
  const { t } = useI18n()

  const labels = ref<SearchKeywordImpressionLabel[]>([])
  const isLoading = ref(false)
  const isSubmitting = ref(false)
  const route = useRoute()
  const { updateFlash } = useFlash()

  const companyUuid = computed(() => String(route.params.company_uuid))

  const readLabels = async () => {
    isLoading.value = true
    try {
      labels.value = await apiClient.getLabels(companyUuid.value)
    } catch (error) {
      console.error(error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateFlash({ type: 'alert', message: (error as any).response.data.errors[0].detail })
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const readLabel = async (labelId: SearchKeywordImpressionLabelId) => {
    isLoading.value = true
    try {
      labels.value = [await apiClient.getLabel(companyUuid.value, labelId)]
    } catch (error) {
      console.error(error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateFlash({ type: 'alert', message: (error as any).response.data.errors[0].detail })
      throw error
    } finally {
      isLoading.value = false
    }
  }

  const updateLabel = async (labelId: SearchKeywordImpressionLabelId, values: SearchKeywordImpressionLabelKeyword[]) => {
    if (isDuplicated(values)) {
      updateFlash({ type: 'alert', message: t('pages.companies.searchKeywordImpressionLabels.messages.duplicatedWords') })
      throw false
    }

    isSubmitting.value = true

    try {
      const updateValues = values.filter((value) => !!value.keyword).map((value) => ({ keyword: value.keyword }))
      await apiClient.updateLabel(companyUuid.value, labelId, updateValues)
      updateFlash({ type: 'notice', message: t('pages.companies.searchKeywordImpressionLabels.messages.updated') })
    } catch (error) {
      console.error(error)
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      updateFlash({ type: 'alert', message: (error as any).response.data.errors[0].detail })
      throw error
    } finally {
      isSubmitting.value = false
    }
  }

  const getLabel = (labelId: SearchKeywordImpressionLabelId) => {
    const label = labels.value.find((label) => label.id === labelId)
    if (!label) return null
    return readonly(label)
  }

  const isDuplicated = (values: SearchKeywordImpressionLabelKeyword[]) => {
    const keywords = values.map((value) => value.keyword)
    const uniqueKeywords = new Set(keywords)
    return keywords.length !== uniqueKeywords.size
  }

  return {
    labels,
    isLoading: readonly(isLoading),
    isSubmitting: readonly(isSubmitting),
    readLabels,
    readLabel,
    updateLabel,
    getLabel,
    companyUuid,
  }
}
